import React, { useState } from "react";
import {
	Backdrop,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { t } from "i18next";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from "axios";
import Loading from "../../../common/loading";
import { setMobileBackColor, setMobileCardColor, setMobileTextColor } from "../../../utility";
import SaveCustomerNameDialog from "../../SaveCustomerNameDialog";
import { useLocation, useNavigate } from "react-router-dom";

const LoginComponent = ({
	open,
	handleCloseDialog,
	tableQr,
	tableName,
	onVerificationSuccess,
	is_from

}) => {
	const [verificationCode, setVerificationCode] = useState("");
	const [loading, setLoading] = useState(false);
	const [phone, setPhone] = useState("");
	const [openVerifiyDialog, setOpenVerifiyDialog] = useState(false);
	const [customerName, setCustomerName] = useState("");
	const [openSaveNameDialog, setOpenSaveNameDialog] = useState(false);
	const [token, setToken] = useState("");

	function useQueryParams() {
		const { search } = useLocation();
		return new URLSearchParams(search);
	}

	const navigate = useNavigate();
	const query = useQueryParams();

	const tParam = query.get("t");
	const lang = query.get("lang");

	const validationSchema = Yup.object().shape({
		phoneNumber: Yup.string()
			.required(t("Phone number is required"))
			.test(
				"isValidPhoneNumber",
				t("Enter a valid phone number"),
				(value) => isValidPhoneNumber(value || "")
			),
	});


	const handleOpenSaveNameDialog = (token) => {
		setToken(token);
		setOpenVerifiyDialog(false);
		setOpenSaveNameDialog(true);
	}

	const saveCustomerName = async () => {
		if (!customerName) {
			toast.error(t("Please enter your name."));
			return;
		}
		setLoading(true);
		try {
			const response = await axios.post(`${process.env.REACT_APP_API}/customer/save-name`, {
				name: customerName,
			}, {
				headers: {
					Authorization: `Bearer ${token}`,
				}
			});
			if (response?.data?.success) {
				toast.success(t("Name updated successfully."));
				onVerificationSuccess(token);
				setOpenSaveNameDialog(false);
			} else {
				toast.error(t("Error while saving the name."));
			}
		} catch (error) {
			toast.error(t("Error while saving the name: ") + error.message);
		} finally {
			setLoading(false);
		}
	};

	const handleSendCode = async (values) => {
		setLoading(true);
		try {
			const response = await axios.post(`${process.env.REACT_APP_API}/customer/send-otp`, {
				phone: values.phoneNumber,
				menu_code: tableQr,
			});

			if (response?.data?.success) {
				setPhone(values.phoneNumber);
				handleCloseDialog();
				setOpenVerifiyDialog(true)
			} else {
				toast.error(t("Error while sending the verification code."));
			}
		} catch (error) {
			toast.error(t("Error while sending the verification code: ") + error.message);
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyCode = async () => {
		setLoading(true);
		try {
			const response = await axios.post(`${process.env.REACT_APP_API}/customer/check-otp`, {
				phone,
				otp: verificationCode,
			});


			if (response?.data?.success) {
				if (response?.data?.data?.name) {
					localStorage.setItem("customer_name", response?.data?.data?.name || "");
					localStorage.setItem("customer_balance", response?.data?.data?.balance || 0);
					onVerificationSuccess(response?.data?.data?.token);
					toast.success(t("Verification successful."));
					setOpenVerifiyDialog(false);

					if (is_from === 1) {
						navigate(`/${tableName}/${tableQr}/sections?section=0&t=${tParam}&lang=${lang}`);

					} else if (is_from === 2) {

						window.location.reload();
					}
				} else {
					handleOpenSaveNameDialog(response?.data?.data?.token);

				}

			} else {
				toast.error(t("Error while verifying the code."));
			}
		} catch (error) {
			toast.error(t("Error while verifying the code: ") + error.message);
		} finally {
			setLoading(false);
		}
	};

	if (loading) {
		return <Loading />;
	}

	return (
		<div>
			<ToastContainer />
			<Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Dialog
				open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">

				<DialogTitle className="head-font">{t("Enter phone number")}</DialogTitle>
				<DialogContent>
					<Formik
						initialValues={{ phoneNumber: "" }}
						validationSchema={validationSchema}
						onSubmit={handleSendCode}
					>
						{({ values, setFieldValue }) => (
							<Form>
								<Field name="phoneNumber">
									{({ field }) => (
										<PhoneInput
											className="sub-font"
											{...field}
											placeholder={t("Enter phone number")}
											value={values.phoneNumber}
											onChange={(value) => setFieldValue("phoneNumber", value)}
											defaultCountry="SA"
											style={{ width: "100%", marginBottom: "20px" }}
										/>
									)}
								</Field>
								<ErrorMessage
									name="phoneNumber"
									component="div"
									style={{ color: "red", marginBottom: "10px" }}
								/>
								<Button className="head-font" type="submit" variant="contained" fullWidth>
									{t("Send Verification Code")}
								</Button>
							</Form>
						)}
					</Formik>

				</DialogContent>
			</Dialog>
			<Dialog className="alignByLang" open={openVerifiyDialog} onClose={() => setOpenVerifiyDialog(false)} fullWidth maxWidth="sm">
				<DialogTitle className="head-font">{t("Enter Verification Code")}</DialogTitle>
				<DialogContent>
					<TextField
						className="sub-font"
						label={<span className="sub-font">{t("Verification Code")}</span>}
						variant="outlined"
						fullWidth
						value={verificationCode}
						onChange={(e) => setVerificationCode(e.target.value)}
						style={{ marginTop: "20px" }}
						sx={{
							".MuiInputBase-input": {
								fontFamily:
									localStorage.getItem("lang") === "ar" ||
										localStorage.getItem("lang") === "fa"
										? "Almarai"
										: "Roboto",
							}
						}}
					/>
				</DialogContent>
				<DialogActions>
					<Button className="head-font" onClick={() => setOpenVerifiyDialog(false)} color="secondary">
						{t("Cancel")}
					</Button>
					<Button className="head-font" onClick={handleVerifyCode} color="primary">
						{t("Verify")}
					</Button>
				</DialogActions>
			</Dialog>

			<SaveCustomerNameDialog openSaveNameDialogVal={openSaveNameDialog} customerName={customerName} setCustomerNameVal={(cust_name) => setCustomerName(cust_name)} handleSaveCustomerName={saveCustomerName} />
		</div>

	);
};

export default LoginComponent;
